import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  NativeSelect,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@/lib/convex";
import { api } from "@convex/_generated/api";
import { PageHeader } from "../components/PageHeader";
import { LoadingCard } from "../components/LoadingCard";
import { STATUS_LEGEND, statusPresentation } from "../components/activityStatus";
import { Tooltip } from "../components/ui/tooltip";
import { PROGRAMME_LABELS, type ProgrammeValue } from "@convex/schemaEnums";
import { oneOf, searchUpdate } from "@/lib/search";
import { FilterCount } from "../components/FilterCount";

export type MatrixSearch = {
  /** PhD when absent. */
  programme?: ProgrammeValue;
  cohort?: number;
};

export function validateMatrixSearch(search: Record<string, unknown>): MatrixSearch {
  return {
    programme: oneOf(search.programme, Object.keys(PROGRAMME_LABELS) as ProgrammeValue[]),
    cohort: typeof search.cohort === "number" && Number.isInteger(search.cohort) ? search.cohort : undefined,
  };
}

function formatDay(day: string | null) {
  if (day === null) {
    return "no date";
  }
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" })
    .format(new Date(`${day}T12:00:00Z`));
}

/**
 * A cohort's progress on one screen: people down, activities across.
 *
 * The programme manager's view. Not another list of everybody's activities —
 * that exists — but the one shape that answers "where is this cohort", which
 * is a question about the group rather than about anyone in it. A column that
 * is mostly grey is a step the programme has a problem with; a row that is
 * mostly grey is a student who does.
 *
 * Cells are squares with an icon rather than labelled chips: at twenty-five
 * people by a dozen activities the pattern is the point, and words at that
 * density read as noise. The full sentence is a tooltip away, and each cell
 * links into the activity it stands for.
 */
export function ProgrammeMatrixPage() {
  const navigate = useNavigate();
  const params = useSearch({ from: "/_authed/activities/admin/progress" });
  const navigateHere = useNavigate({ from: "/activities/admin/progress" });
  const programme: ProgrammeValue = params.programme ?? "phd";
  const cohort = params.cohort === undefined ? "all" : String(params.cohort);
  const setParams = (patch: MatrixSearch) =>
    void navigateHere({ search: (prev) => ({ ...prev, ...patch }), ...searchUpdate });

  const data = useQuery(api.programmeProgress.matrix, {
    programme,
    cohort: params.cohort ?? null,
  });

  const breadcrumbs = [
    { label: "CeMMbase", to: "/" },
    { label: "Activities", to: "/activities" },
    { label: "Programme progress" },
  ];

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Programme progress"
        description="Where a cohort has got to: people down, programme activities across. Current and interrupted enrollments only — a finished one is history."
        breadcrumbs={breadcrumbs}
        actions={data === undefined ? undefined : (
          <HStack gap={2} flexWrap="wrap">
            {/* Sparseness as a number: an enrolled student with nothing
                assigned is a finding, not whitespace to be noticed. */}
            {data.unassigned > 0 ? (
              <Badge colorPalette="orange" px={3} py={1.5} textTransform="uppercase" letterSpacing="0.16em">
                {data.unassigned} with no programme activity
              </Badge>
            ) : null}
          </HStack>
        )}
      />

      <Stack gap={5} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        <Flex gap={3} flexWrap="wrap" align="center">
          {/* One programme at a time. The two share almost no activities, so
              a combined view would be a grid of holes. */}
          <NativeSelect.Root size="sm" w="auto" minW="36">
            <NativeSelect.Field
              value={programme}
              onChange={(e) => {
                const value = e.target.value as ProgrammeValue;
                // A cohort belongs to one programme; it does not carry over.
                setParams({ programme: value === "phd" ? undefined : value, cohort: undefined });
              }}
            >
              {Object.entries(PROGRAMME_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <NativeSelect.Root size="sm" w="auto" minW="36">
            <NativeSelect.Field
              value={cohort}
              onChange={(e) => setParams({ cohort: e.target.value === "all" ? undefined : Number(e.target.value) })}
            >
              <option value="all">All cohorts</option>
              {(data?.cohorts ?? []).map((value) => (
                <option key={value} value={String(value)}>Cohort {value}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <Box flex="1" />

          {/* Colour says how it is going; the icon says which state. Either
              alone names it, so neither has to be read perfectly. */}
          <HStack gap={3} flexWrap="wrap">
            {STATUS_LEGEND.map((entry) => {
              const look = statusPresentation(entry.status, entry.overdue);
              return (
                <HStack key={`${entry.status}-${entry.overdue ? "late" : ""}`} gap={1.5}>
                  <Flex
                    w="5"
                    h="5"
                    align="center"
                    justify="center"
                    colorPalette={look.tone}
                    bg="colorPalette.subtle"
                    color="colorPalette.fg"
                    borderWidth="1px"
                    borderColor="colorPalette.muted"
                  >
                    <Icon as={look.icon} boxSize={3} />
                  </Flex>
                  <Text fontSize="xs" color="fg.muted">{look.label}</Text>
                </HStack>
              );
            })}
          </HStack>
        </Flex>

        {/* Counted on the server for one cohort at a time, so there is no
            total to set it against. The programme is a choice of view, not a
            filter: clearing leaves it. */}
        {data === undefined ? null : (
          <FilterCount
            shown={data.people.length}
            total={null}
            noun={["person", "people"]}
            filtered={params.cohort !== undefined}
            onClear={() => setParams({ cohort: undefined })}
          >
            {` · ${data.columns.length} ${data.columns.length === 1 ? "activity" : "activities"}`}
          </FilterCount>
        )}

        {data === undefined ? (
          <LoadingCard label="Loading the cohort…" />
        ) : data.people.length === 0 ? (
          <Text fontSize="sm" color="fg.muted">
            Nobody is enrolled under that filter.
          </Text>
        ) : (
          // The scroll container is what `position: sticky` resolves against,
          // so it has to be the thing with a height — otherwise the header
          // sticks to a viewport it never scrolls within.
          <Box
            overflow="auto"
            maxH="70vh"
            borderWidth="1px"
            borderColor="border"
            bg="bg"
          >
            <Table.Root size="sm">
              {/* A pinned row sits over the body and so needs its own
                  bottom edge. An inset shadow rather than a border: with
                  border-collapse the header's border belongs to the
                  collapsed grid and scrolls away with the rows, leaving
                  the pinned row edgeless. Same fix as `DataTable`. */}
              <Table.Header
                css={{
                  "& th": {
                    boxShadow: "inset 0 -1px 0 var(--chakra-colors-border)",
                  },
                }}
              >
                <Table.Row>
                  <Table.ColumnHeader
                    position="sticky"
                    top={0}
                    left={0}
                    // Above the sticky body cells, which are themselves above
                    // the ordinary ones: this corner overlaps both.
                    zIndex={3}
                    bg="bg"
                    minW="56"
                    verticalAlign="bottom"
                  >
                    Person
                  </Table.ColumnHeader>

                  {data.columns.map((column) => (
                    <Table.ColumnHeader
                      key={column.activityId}
                      position="sticky"
                      top={0}
                      zIndex={2}
                      bg="bg"
                      w="10"
                      p={1}
                      verticalAlign="bottom"
                    >
                      {/* Set vertically rather than rotated. A rotated label
                          overflows its own column, and each sticky header cell
                          paints a background over its neighbour's overflow --
                          which is what was clipping the titles. Vertical text
                          stays inside its column, so it can be ellipsised
                          honestly and nothing is painted over. Full text on
                          hover. */}
                      <Tooltip content={column.title}>
                        <Text
                          h="40"
                          maxH="40"
                          mx="auto"
                          writingMode="vertical-rl"
                          transform="rotate(180deg)"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          cursor="default"
                          fontSize="xs"
                          fontWeight="normal"
                          textTransform="none"
                          letterSpacing="normal"
                          color="fg.muted"
                        >
                          {column.title}
                        </Text>
                      </Tooltip>
                    </Table.ColumnHeader>
                  ))}

                  <Table.ColumnHeader
                    position="sticky"
                    top={0}
                    zIndex={2}
                    bg="bg"
                    w="16"
                    textAlign="right"
                    verticalAlign="bottom"
                  >
                    Done
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {data.people.map((person) => (
                  <Table.Row key={String(person.personId)}>
                    <Table.Cell position="sticky" left={0} zIndex={1} bg="bg" minW="56">
                      <Link
                        to="/programme/$personId"
                        params={{ personId: String(person.personId) }}
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          _hover={{ textDecoration: "underline" }}
                        >
                          {person.personName}
                        </Text>
                      </Link>
                      <Text fontSize="xs" color="fg.muted">
                        Year {person.year}
                        {person.cohort === null ? "" : ` · ${person.cohort}`}
                        {person.overdue > 0 ? ` · ${person.overdue} late` : ""}
                      </Text>
                    </Table.Cell>

                    {data.columns.map((column) => {
                      const cell = person.cells[column.activityId];
                      if (cell === undefined) {
                        // Not assigned. A dashed outline rather than a grey
                        // square: an absence is what a manager is looking
                        // for, and it must not look like a status.
                        return (
                          <Table.Cell key={column.activityId} w="10" p={1}>
                            <Tooltip
                              content={
                                <>
                                  <Text fontWeight="medium">{column.title}</Text>
                                  <Text>Not assigned to {person.personName}</Text>
                                </>
                              }
                            >
                              <Box
                                w="6"
                                h="6"
                                borderWidth="1px"
                                borderStyle="dashed"
                                borderColor="border"
                              />
                            </Tooltip>
                          </Table.Cell>
                        );
                      }

                      const look = statusPresentation(cell.status, cell.overdue);
                      return (
                        <Table.Cell key={column.activityId} w="10" p={1}>
                          {/* Chakra tooltips, kept after checking what they
                              cost here rather than in the abstract: hovering
                              re-renders the hovered cell and the tooltip and
                              nothing else. The measurement that argued against
                              them was of MOUNT cost across a full grid, which
                              the cohort filter keeps small in practice. */}
                          <Tooltip
                            content={
                              <>
                                <Text fontWeight="medium">{column.title}</Text>
                                <Text>{person.personName}</Text>
                                <Text>{look.label} · due {formatDay(cell.dueDay)}</Text>
                              </>
                            }
                          >
                          <Flex
                            as="button"
                            w="6"
                            h="6"
                            align="center"
                            justify="center"
                            colorPalette={look.tone}
                            bg="colorPalette.subtle"
                            color="colorPalette.fg"
                            borderWidth="1px"
                            borderColor="colorPalette.muted"
                            cursor="pointer"
                            aria-label={`${person.personName}, ${column.title}: ${look.label}`}
                            onClick={() => void navigate({
                              to: "/activities/$personActivityId",
                              params: { personActivityId: String(cell.personActivityId) },
                            })}
                          >
                            <Icon as={look.icon} boxSize={3} />
                          </Flex>
                          </Tooltip>
                        </Table.Cell>
                      );
                    })}

                    <Table.Cell w="16" textAlign="right">
                      <Text fontSize="xs" color="fg.muted" fontVariantNumeric="tabular-nums">
                        {person.done}/{person.total}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
